/**
 * AdminService - Frontend API client for admin features.
 * Covers accelerator catalog, provider credentials, and log viewing.
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://api.tcf-studio.dev';

const getHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * Normalize a fetch Response. On success returns the parsed JSON body.
 * On a non-OK status returns a typed error object with a `forbidden` flag
 * so callers can distinguish a 403 (admin-only) from a generic failure and
 * render a clean message rather than a generic error.
 */
const parseResponse = async (res) => {
  let body = {};
  try {
    body = await res.json();
  } catch {
    body = {};
  }
  if (!res.ok) {
    const forbidden = res.status === 403;
    return {
      success: false,
      status: res.status,
      forbidden,
      error: body.error || body.detail || (forbidden
        ? 'This action is restricted to admins.'
        : `Request failed (${res.status})`),
    };
  }
  return body;
};

// ── Accelerator Catalog ──────────────────────────────────────────

export const getAcceleratorCatalog = async () => {
  const res = await fetch(`${API_URL}/admin/accelerators`, { headers: getHeaders() });
  return parseResponse(res);
};

export const refreshAcceleratorCatalog = async () => {
  const res = await fetch(`${API_URL}/admin/accelerators/refresh`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return parseResponse(res);
};

// ── Provider Credentials ─────────────────────────────────────────

export const getProviderCredentials = async () => {
  const res = await fetch(`${API_URL}/admin/providers`, { headers: getHeaders() });
  return parseResponse(res);
};

export const getProviderCredential = async (provider) => {
  const res = await fetch(`${API_URL}/admin/providers/${provider}`, { headers: getHeaders() });
  return parseResponse(res);
};

export const saveProviderCredential = async (provider, credentials) => {
  const res = await fetch(`${API_URL}/admin/providers/${provider}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(credentials),
  });
  return parseResponse(res);
};

export const deleteProviderCredential = async (provider) => {
  const res = await fetch(`${API_URL}/admin/providers/${provider}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return parseResponse(res);
};

// ── Orchestrator Logs ────────────────────────────────────────────

export const getServerLogs = async ({ lines = 200, level = null, search = null } = {}) => {
  const params = new URLSearchParams({ lines });
  if (level) params.set('level', level);
  if (search) params.set('search', search);
  const res = await fetch(`${API_URL}/admin/logs/server?${params}`, { headers: getHeaders() });
  return parseResponse(res);
};

export const getClusterLogs = async (clusterName, { lines = 200 } = {}) => {
  const params = new URLSearchParams({ lines, cluster: clusterName });
  const res = await fetch(`${API_URL}/admin/logs/cluster?${params}`, { headers: getHeaders() });
  return parseResponse(res);
};

// ── GPU Config ──────────────────────────────────────────────────

export const getGpuConfigs = async (provider = null) => {
  const params = provider ? `?provider=${provider}` : '';
  const res = await fetch(`${API_URL}/admin/gpu-config${params}`, { headers: getHeaders() });
  return parseResponse(res);
};

export const upsertGpuConfig = async (config) => {
  const res = await fetch(`${API_URL}/admin/gpu-config`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(config),
  });
  return parseResponse(res);
};

export const bulkUpsertGpuConfigs = async (configs) => {
  const res = await fetch(`${API_URL}/admin/gpu-config/bulk`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ configs }),
  });
  return parseResponse(res);
};

export const deleteGpuConfig = async (id) => {
  const res = await fetch(`${API_URL}/admin/gpu-config/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return parseResponse(res);
};

export const deleteAllGpuConfigs = async () => {
  const res = await fetch(`${API_URL}/admin/gpu-config/all`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return parseResponse(res);
};

export const getGpuOverrides = async () => {
  const res = await fetch(`${API_URL}/admin/gpu-config/overrides`, { headers: getHeaders() });
  return parseResponse(res);
};

export const upsertGpuOverride = async (override) => {
  const res = await fetch(`${API_URL}/admin/gpu-config/overrides`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(override),
  });
  return parseResponse(res);
};

export const deleteGpuOverride = async (id) => {
  const res = await fetch(`${API_URL}/admin/gpu-config/overrides/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return parseResponse(res);
};

// --- SSH Node Pools ---

export const getSshNodePools = async () => {
  const res = await fetch(`${API_URL}/admin/ssh-pools`, { headers: getHeaders() });
  return parseResponse(res);
};

export const updateSshNodePool = async (poolName, config) => {
  const res = await fetch(`${API_URL}/admin/ssh-pools`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ [poolName]: config }),
  });
  return parseResponse(res);
};

export const deleteSshNodePool = async (poolName) => {
  const res = await fetch(`${API_URL}/admin/ssh-pools/${encodeURIComponent(poolName)}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return parseResponse(res);
};

export const getSshNodePoolStatus = async (poolName) => {
  const res = await fetch(`${API_URL}/admin/ssh-pools/${encodeURIComponent(poolName)}/status`, {
    headers: getHeaders(),
  });
  return parseResponse(res);
};

export const deploySshNodePool = async (poolName) => {
  const res = await fetch(`${API_URL}/admin/ssh-pools/${encodeURIComponent(poolName)}/deploy`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return parseResponse(res);
};

export const teardownSshNodePool = async (poolName) => {
  const res = await fetch(`${API_URL}/admin/ssh-pools/${encodeURIComponent(poolName)}/down`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return parseResponse(res);
};

export const listSshKeys = async () => {
  const res = await fetch(`${API_URL}/admin/ssh-pools/keys`, { headers: getHeaders() });
  return parseResponse(res);
};
